import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIcon, IPStack, IPText } from 'baseComponents';
import { globalFontBoldWeight, globalStackTokensGapSmall } from 'globalStyles';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IAddNewGenericButton {
  disabled?: boolean;
  icon: JSX.Element;
  sideText: string;
  belowText: string;
  selected: boolean;
  onClick: () => void;
}

const AddNewGenericButton = (props: IAddNewGenericButton) => {
  const [hovered, setHovered] = useState<boolean>(false);
  const appContext = useContext(AppContext);
  const theme = getTheme();

  const getButtonForeground = (): string => {
    if (props.selected) return theme.palette.white;

    return appContext.useDarkMode ? theme.palette.neutralLight : theme.palette.neutralDark;
  };

  const getButtonBackground = (): string => {
    if (props.disabled) return appContext.useDarkMode ? theme.palette.neutralDark : theme.palette.neutralLight;
    if (props.selected) return theme.palette.themePrimary;

    if (hovered) return appContext.useDarkMode ? theme.palette.neutralPrimaryAlt : theme.palette.themeLight;

    return appContext.useDarkMode ? theme.palette.neutralPrimary : theme.palette.themeLighterAlt;
  };

  return (
    <IPStack
      tabIndex={0}
      onKeyDown={(ev) => {
        if (!props.disabled && (ev.key === 'Enter' || ev.key === ' ')) {
          props.onClick();
        }
      }}
      onClick={() => {
        if (!props.disabled) {
          props.onClick();
        }
      }}
      styles={{
        root: {
          width: 200,
          height: 120,
          borderWidth: 1,
          borderStyle: 'solid',
          backgroundColor: getButtonBackground(),
          padding: 10,
        },
      }}
      onMouseOver={() => {
        if (!props.disabled) setHovered(true);
      }}
      onMouseLeave={() => {
        if (!props.disabled) setHovered(false);
      }}
    >
      <IPStack.Item style={{ height: '50%' }}>
        <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPStack.Item>{props.icon}</IPStack.Item>
          <IPStack.Item>
            <IPText
              styles={{ root: { fontWeight: globalFontBoldWeight, color: getButtonForeground() } }}
              variant="mediumPlus"
            >
              {props.sideText}
            </IPText>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item style={{ height: '50%' }}>
        <IPText styles={{ root: { color: getButtonForeground() } }}>{props.belowText}</IPText>
      </IPStack.Item>
    </IPStack>
  );
};

interface IAddNewListButtonProps {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const iconSize = 30;

export const AddNewDocumentLibraryButton = (props: IAddNewListButtonProps) => {
  const { t } = useTranslation(['translation', 'lists']);

  return (
    <AddNewGenericButton
      disabled={props.disabled}
      onClick={props.onClick}
      icon={<IPIcon iconName="DocLibrary" style={{ fontSize: iconSize }} />}
      belowText={t('lists:AddNew.DocumentLibraryInfo')}
      selected={props.selected}
      sideText={t('lists:AddNew.DocumentLibraryTitle')}
    />
  );
};

export const AddNewCustomListButton = (props: IAddNewListButtonProps) => {
  const { t } = useTranslation(['translation', 'lists']);

  return (
    <AddNewGenericButton
      disabled={props.disabled}
      onClick={props.onClick}
      icon={<IPIcon iconName="CustomList" style={{ fontSize: iconSize }} />}
      belowText={t('lists:AddNew.CustomListInfo')}
      selected={props.selected}
      sideText={t('lists:AddNew.CustomListTitle')}
    />
  );
};

export const AddNewSitePageLibraryButton = (props: IAddNewListButtonProps) => {
  const { t } = useTranslation(['translation', 'lists']);

  return (
    <AddNewGenericButton
      disabled={props.disabled}
      onClick={props.onClick}
      icon={<IPIcon iconName="Page" style={{ fontSize: iconSize }} />}
      belowText={t('lists:AddNew.SitePageLibraryInfo')}
      selected={props.selected}
      sideText={t('lists:AddNew.SitePageLibraryTitle')}
    />
  );
};

/** A category on a connected document provider: Google Drive, Nextcloud or Notion. */
export const AddNewProviderListButton = (props: IAddNewListButtonProps) => {
  const { t } = useTranslation(['documentproviders']);

  return (
    <AddNewGenericButton
      disabled={props.disabled}
      onClick={props.onClick}
      icon={<IPIcon iconName="CloudImportExport" style={{ fontSize: iconSize }} />}
      belowText={t('documentproviders:Admin.SubHeading')}
      selected={props.selected}
      sideText={t('documentproviders:Admin.Heading')}
    />
  );
};

export const AddNewUrlListButton = (props: IAddNewListButtonProps) => {
  const { t } = useTranslation(['translation', 'lists']);

  return (
    <AddNewGenericButton
      disabled={props.disabled}
      onClick={props.onClick}
      icon={<IPIcon iconName="Globe" style={{ fontSize: iconSize }} />}
      belowText={t('lists:AddNew.URLListInfo')}
      selected={props.selected}
      sideText={t('lists:AddNew.URLListTitle')}
    />
  );
};
